import type { GpbBinding } from './readEvidence';
import { evaluateRank } from './evaluateGpb';
import type { GpbDefinition, GpbEvidence, GpbRankResult } from '../../types/gpb';

/**
 * ⭐⭐ **ONE PERSON'S RUN AT ONE BUILD.** The definition is shared and versioned;
 * this is what belongs to them — when they started, at what commitment, which
 * native objects their authored keys point at, and when each rank's window
 * opened.
 *
 * ⚠️⚠️ **THE VERSION IS PINNED AND A NEW ONE NEVER REWRITES IT.** The package is
 * explicit that a started build keeps its content: "do not silently rewrite the
 * conditions for a rank someone has already completed." An author's update is an
 * offer, and migrating is a separate decision with its own preview.
 */
export interface GpbEnrollment {
  id: string;
  definitionId: string;
  /** ⚠️ The version they started, not the latest. */
  version: string;
  /** IANA zone captured at enrollment — windows are LOCAL dates in it. */
  timezone: string;
  /** D4: the same pathway at a different pace. Changing it invalidates nothing. */
  commitment: 'full' | 'steady' | 'light';
  status: 'active' | 'completed' | 'archived';
  /**
   * Rank number → the local date its window opened. A rank absent from here has
   * not been reached, and its content is previewable but counts nothing.
   */
  windows: Readonly<Record<number, string>>;
  binding: GpbBinding;
}

/**
 * The local day after `date`.
 *
 * ⚠️⚠️ **PLAIN DATE ARITHMETIC, NOT `new Date(string)`.** `new Date('2026-03-01')`
 * parses as UTC midnight, so adding a day and formatting locally shifts the
 * result by one in every timezone behind UTC — the window would open a day early
 * for half the world. Parsed and reassembled through `Date.UTC`, this is the same
 * answer everywhere, which is what a stored local date needs to be.
 */
export function nextLocalDay(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  const at = new Date(Date.UTC(y, m - 1, d + 1));
  return `${at.getUTCFullYear()}-${String(at.getUTCMonth() + 1).padStart(2, '0')}-${String(at.getUTCDate()).padStart(2, '0')}`;
}

/** A build just started: rank 1 opens today, nothing else is reached. */
export function enroll(
  def: GpbDefinition, today: string, binding: GpbBinding,
  opts: { id: string; timezone: string; commitment: GpbEnrollment['commitment'] },
): GpbEnrollment {
  return {
    id: opts.id,
    definitionId: def.id,
    version: def.version,
    timezone: opts.timezone,
    commitment: opts.commitment,
    status: 'active',
    windows: { 1: today },
    binding,
  };
}

/**
 * Open the next rank's window.
 *
 * ⚠️⚠️ **THE NEXT LOCAL MIDNIGHT AFTER THE RANK COMPLETED — NOT AFTER THE APP
 * NOTICED.** The brief: "on verified completion of Rank N, expose Rank N+1
 * content immediately and persist its fresh window starting at the next local
 * midnight." Someone who finishes on the 5th and opens the app on the 9th has a
 * window from the 6th; keying it to the observation would silently discard four
 * days of their practice.
 *
 * ⚠️ Idempotent. A rank whose window is already open is left exactly as it was,
 * because re-opening it would move the floor and un-count work already done.
 */
export function openNextRank(
  enrollment: GpbEnrollment, completedRank: number, completedOn: string, def: GpbDefinition,
): GpbEnrollment {
  const next = completedRank + 1;
  if (enrollment.windows[next]) return enrollment;
  if (!def.ranks.some(r => r.rank === next)) {
    /* ⚠️ Rank 7 ends the pathway: "completing Rank 7 ends this pathway." No
       rank 8, no reset, and the practices simply continue. */
    return { ...enrollment, status: 'completed' };
  }
  return { ...enrollment, windows: { ...enrollment.windows, [next]: nextLocalDay(completedOn) } };
}

/**
 * Open every window the evidence has earned.
 *
 * ⭐⭐ **THE COMPLETION DATE NOW COMES FROM THE EVIDENCE, NOT THE CALLER.** Each
 * rank reports when its last requirement was met, so the next window opens the
 * day after THAT — no longer the day the app happened to look. Applied in order,
 * because rank 3 cannot open until rank 2's window exists to be evaluated.
 */
export function advance(
  enrollment: GpbEnrollment, def: GpbDefinition, evidence: GpbEvidence,
): GpbEnrollment {
  let at = enrollment;
  for (const rank of def.ranks) {
    const window = at.windows[rank.rank];
    if (!window) break;
    const result = evaluateRank(rank, evidence, window, def);
    if (!result.done || !result.completedOn) break;
    at = openNextRank(at, rank.rank, result.completedOn, def);
  }
  return at;
}

export interface GpbStanding {
  /** The rank they are working on, or 'completed' when the pathway is finished. */
  current: number | 'completed';
  /** One per rank. A rank not yet reached is `null` — previewable, counting nothing. */
  ranks: readonly (GpbRankResult | null)[];
}

/**
 * Where they stand: every reached rank evaluated against **its own window**.
 *
 * ⚠️⚠️ Each rank counts from a different date, so they cannot share one
 * evaluation. A rank with no window is not evaluated at all — showing it as
 * "0 of 3" would invite someone to start work that will not count until its rank
 * opens.
 */
export function standingOf(
  enrollment: GpbEnrollment, def: GpbDefinition, evidence: GpbEvidence,
): GpbStanding {
  const ranks = def.ranks.map(r => {
    const window = enrollment.windows[r.rank];
    return window ? evaluateRank(r, evidence, window, def) : null;
  });
  const unfinished = ranks.findIndex(r => r === null || !r.done);
  return {
    ranks,
    current: unfinished === -1 ? 'completed' : def.ranks[unfinished].rank,
  };
}
